import { DEMO_PROJECT } from './demo-project';
import type { GgxTourDefinition } from './tour-types';

export const HOWTO_09: GgxTourDefinition = {
    key: '[HOWTO-09] Standardizing your Reports',
    route: '/home',
    project: DEMO_PROJECT,
    estimatedMinutes: 4,
    prerequisite: {
        target: '[test-id="home-page-card-Data & AI Assets"]',
        message: 'Open the home page, then start this tour.',
    },
    intro: {
        headline: 'One definition of accuracy, for everyone',
        subtitle:
            'When two teams measure "accuracy" differently, their numbers cannot be compared ' +
            'and nobody can say which pipeline is better. GGX fixes that with reports: a metric ' +
            'is written once, reviewed, approved and then locked, so every evaluation in the ' +
            'organization uses the same definition. This tour opens one approved report and ' +
            'shows you what makes it reusable and what "locked" really means.',
    },
    outro: {
        title: "That's a wrap!",
        subtitle:
            'You opened Response Accuracy (Credit Card Queries) and saw the four things that ' +
            'make a report a standard rather than a one-off script: a description that states ' +
            'exactly what data it needs, parameters that let the same report run on different ' +
            'datasets, a recommended dataset so everyone evaluates against the same rows, and ' +
            'shared, versioned building blocks behind the metric. Once approved, the report is ' +
            'locked: it runs from the Run menu of any pipeline, and changing it means cloning ' +
            'a new version while the original keeps working for everyone who depends on it. ' +
            'The next tour, Approvals for Reports, shows how a report earns that lock.',
        learned: [
            'A standard report states **what data it needs** in its description.',
            '**Parameters and a recommended dataset** let everyone measure the same way.',
            'Once approved it is **locked**; changes become a new version.',
        ],
    },
    chapters: [
        {
            name: 'Find the report',
            blurb: 'From the home page to one approved report',
            icon: 'map',
            steps: [2, 3, 4, 5],
        },
        {
            name: 'What makes it reusable',
            blurb: 'Description, parameters, data and building blocks',
            icon: 'grid',
            steps: [6, 7, 8, 9],
        },
        {
            name: 'Locked and trusted',
            blurb: 'What approval changes, and how to evolve a report',
            icon: 'compass',
            steps: [10, 11],
        },
    ],
    steps: [
        {
            target: '[test-id="home-page-card-Data & AI Assets"] [test-id="launch-button"]',
            title: 'Reports live with your other AI assets',
            whatHappens:
                'the module opens on the Agents page, with Reports further down its sidebar.',
            icon: 'map',
            description:
                "A report is the piece of GGX that turns a pipeline's answers into a score. " +
                'Reports are kept in <strong>Data & AI Assets</strong>, next to the models, ' +
                'prompts, pipelines and global functions they measure and are built from, so ' +
                'the metric lives beside the things it scores. Click <strong>Launch</strong> on ' +
                'this card.',
            side: 'bottom',
            align: 'center',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Launch',
        },
        {
            target: '[test-id="sidenav-item-tab-Reports"]',
            scrollIntoView: true,
            title: 'Open the Reports page',
            whatHappens:
                'the Reports page opens, with every report grouped by the risk it measures.',
            icon: 'map',
            description:
                'The sidebar lists everything this module manages. <strong>Reports</strong> is ' +
                'the catalogue of reusable metrics for the whole project: every evaluation you ' +
                'run picks its report from here, which is what keeps the numbers comparable. ' +
                'Click <strong>Reports</strong>.',
            side: 'right',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Reports',
        },
        {
            target: '[test-id^="group-Accuracy Reports"]',
            targetText: 'Accuracy Reports',
            climbToRow: true,
            waitForPresent: '[test-id="report-object-filter"]',
            advanceWhenPresent: '[test-id^="grid-row-link-Response Accuracy"]',
            title: 'Reports are grouped by the risk they measure',
            whatHappens: 'the group expands to list every accuracy report.',
            icon: 'grid',
            description:
                'Each group here is a kind of risk: accuracy, toxicity, vulnerability and so on, ' +
                'with the count of reports beside it. That grouping is the first layer of ' +
                'standardization: when someone asks "how do we measure accuracy here?", the ' +
                'answer is one folder, not a search through notebooks. Click <strong>Accuracy ' +
                'Reports</strong> to open the group.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Accuracy Reports',
        },
        {
            target: '[test-id^="grid-row-link-Response Accuracy"]',
            targetText: 'Response Accuracy (Credit Card Queries)',
            climbToRow: true,
            scrollIntoView: true,
            title: 'Some reports fit any pipeline, some fit one kind',
            whatHappens: "the report's page opens on its Details tab.",
            icon: 'search',
            description:
                'A toxicity report can score any pipeline, because it only needs the reply. ' +
                '<strong>Response Accuracy (Credit Card Queries)</strong> is more specific: it ' +
                'checks each answer against the card facts the pipeline looked up, so it only ' +
                'makes sense for a pipeline that does that lookup, like the Combined Customer ' +
                'Intent and Response Pipeline. Notice it is <strong>Approved</strong> while most ' +
                'of its neighbours are drafts. Click it to open it.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Response Accuracy (Credit Card Queries)',
        },
        {
            target: '[test-id="description"]',
            frame: true,
            scrollIntoView: true,
            title: 'The description is a contract',
            whatHappens: 'next, the parameters that let this report run on different datasets.',
            icon: 'list',
            description:
                'Read it as a contract rather than a blurb. It states what the report measures ' +
                '(answer relevancy and factual correctness), and exactly which columns your data ' +
                'must have: <strong>user_message</strong>, <strong>ground_truth_facts</strong> ' +
                'and <strong>intent</strong>. Below it, the risk type, task type and evaluation ' +
                'methodology (LLM-as-a-Judge) tell a reviewer how the score is produced. Anyone ' +
                'who runs this report is agreeing to those terms, which is why two teams get ' +
                'comparable numbers. Click Continue.',
            side: 'top',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="parameters-schema"]',
            frame: true,
            scrollIntoView: true,
            title: 'Parameters: one report, many datasets',
            whatHappens: 'next, the dataset the report recommends.',
            icon: 'list',
            description:
                'Parameters are the values the person running the report fills in at run time. ' +
                'Here there are two: which column holds the <strong>intent</strong> and which ' +
                'holds the <strong>ground truth facts</strong>. That is what makes the report ' +
                'reusable: the logic never changes, only the pointers to your data, so the same ' +
                "report can score last quarter's validation set and next week's new " +
                'questions and the results line up. Click Continue.',
            side: 'top',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="report-data-source-examples"]',
            frame: true,
            scrollIntoView: true,
            title: 'A recommended dataset, so everyone tests on the same rows',
            whatHappens: 'next, the building blocks the metric is made from.',
            icon: 'clock',
            description:
                'The report author attached <strong>Credit Card Related Response Accuracy ' +
                'Data</strong> as an example data source. This is the dataset GGX selected for ' +
                'you automatically in the evaluation tour. With the dataset registered in the ' +
                'Table Registry, the questions are fixed too, so a score of 92 this month and 88 ' +
                'next month is a real change in the pipeline, not a change in the test. Click ' +
                'Continue.',
            side: 'top',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="report-code"] [test-id="resources"]',
            scrollIntoView: true,
            title: 'The metric is built from shared, versioned pieces',
            whatHappens: 'next, what approval does to a report.',
            icon: 'pulse',
            description:
                'Look at the two chips above the code. <strong>response_fact_check_with_' +
                'ground_truth_facts</strong> is a global function that compares an answer with ' +
                'the card facts, and <strong>answer_relevancy_cc_pipeline_llm_as_a_judge</strong> ' +
                'is the judge model that rates relevance. Both are registered objects with their ' +
                'own versions and approvals. Several reports can use the same function, so when ' +
                'the fact check improves, every report built on it improves in the same way, and ' +
                'the code here only stitches them together. Click Continue.',
            side: 'top',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="report-action-buttons"]',
            title: 'Approved means locked. Clone is how it evolves',
            whatHappens: 'next, where you can see who approved it.',
            icon: 'compass',
            description:
                'A report moves from Draft to Pending Approval to Approved. Once approved it is ' +
                'locked: its logic, parameters and figures cannot change underneath the people ' +
                'relying on it, and <strong>Run</strong> uses it exactly as reviewed. To improve ' +
                'it you <strong>Clone</strong> it, which starts a new draft version, while the ' +
                'approved one keeps working everywhere it is used. <strong>Copy</strong> makes ' +
                'your own separate report to experiment with. Click Continue.',
            side: 'bottom',
            align: 'end',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="tab-item-Approvals"]',
            scrollIntoView: true,
            title: 'The lock has a paper trail',
            whatHappens: 'the tour wraps up, and the next one shows how a report earns approval.',
            icon: 'map',
            description:
                'The <strong>Approvals</strong> tab records who reviewed this report, what they ' +
                'decided and when. That is what makes an approved report trustworthy to an ' +
                'auditor and not just convenient to a developer. No need to open it now; the ' +
                'Approvals for Reports tour walks through the whole review. Click Continue to ' +
                'finish.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'next'],
        },
    ],
};
